"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateFormField = exports.isValidPassword = exports.isEmailValid = void 0;
const constant_1 = require("@/constants/constant");
/**
 * Validates an email address against a regular expression and a list of excluded domains.
 *
 * @param {string} emailValue - The email address to validate.
 * @param {string[]} excludedDomains - The list of domains to exclude.
 * @returns {[boolean, string]} - A tuple where the first element is a boolean indicating whether the email is valid, and the second element is an error message if the email is invalid, or an empty string if the email is valid.
 */
const isEmailValid = (emailValue, excludedDomains) => {
    const emailValidationRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isEmailValid = emailValidationRegex.test(emailValue);
    const isExcludedDomain = excludedDomains.some((domain) => emailValue.trim().toLowerCase().includes(domain));
    if (!emailValue) {
        return [false, constant_1.EMAIL_REQUIRED_ERROR];
    }
    else if (!isEmailValid) {
        return [false, constant_1.EMAIL_VALIDATION_ERROR];
    }
    else if (isExcludedDomain) {
        return [false, constant_1.EMAIL_BUSINESS_EMAIL_ERROR];
    }
    return [true, ''];
};
exports.isEmailValid = isEmailValid;
/**
 * Validates a password against several requirements based on https://github.com/eMarketer/eMarketer.TotalAccess.Subscriptions/blob/master/Web/Security/TrackAuthentications.cs#L304-L331
 * Any changes in here or the backend system should be synched with the other.
 *
 * @param {string} password - The password to validate.
 * @returns {boolean} - Returns true if the password meets all the requirements, or false otherwise.
 */
const isValidPassword = (password) => {
    if (password.length < 7 || password.length > 14) {
        return [false, constant_1.PASSWORD_LENGTH_ERROR];
    }
    // Requires at least one letter
    if (!/[a-zA-Z]/.test(password)) {
        return [false, constant_1.PASSWORD_AT_LEAST_ONE_LETTER_AND_NUMBER_ERROR];
    }
    // Requires at least one numeric digit
    if (!/[0-9]/.test(password)) {
        return [false, constant_1.PASSWORD_AT_LEAST_ONE_LETTER_AND_NUMBER_ERROR];
    }
    // No "emarketer" or "password" allowed
    if (password.includes('emarketer') || password.includes('password')) {
        return [false, constant_1.PASSWORD_RESERVED_WORD_ERROR];
    }
    return [true, ''];
};
exports.isValidPassword = isValidPassword;
/**
 * Validates the length and format of a given string.
 * @param {string} fieldName - The name of the field being validated.
 * @param {string} input - The string to validate.
 * @param {number} maxLength - The maximum allowed length.
 * @param {number} [minLength=1] - The minimum allowed length (default is 1).
 * @param {RegExp | null} [regex=null] - The regular expression to validate the format (default is null).
 * @returns {[boolean, string]} - A tuple where the first element is a boolean indicating whether the input is valid, and the second element is an error message if the input is invalid, or an empty string if the input is valid.
 */
const validateFormField = (fieldName, input, maxLength, minLength = 1, regex = null) => {
    input = input?.trim();
    if (input?.length > maxLength || input?.length < minLength) {
        return [false, `${fieldName} must be between ${minLength} and ${maxLength} characters.`];
    }
    if (regex && !regex.test(input)) {
        return [false, `Please enter a valid ${fieldName.toLowerCase()}.`];
    }
    return [true, ''];
};
exports.validateFormField = validateFormField;
